import React, { useState, useEffect } from "react"
import classNames from "classnames"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

import "§/src/components/contact-modal/_contact-form.scss"

import UniversalLink from "§/src/utils/UniversalLink"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import CloseIcon from "§/src/images/svg/close.svg"

// ATTENZIONE — assunzione da verificare prima del deploy:
// gli ID dei campi qui sotto sono stati forniti a mano da Giorgio (form Gravity Forms id 11).
// Per le checkbox (input_6, input_23, input_24) non è stato possibile confermare il sotto-ID
// dell'opzione contro la definizione reale del form (credenziali CONTACTI_URL/CONTACTI_APP_USER/
// CONTACTI_APP_PASS non disponibili in locale): si è assunto ".1" (opzione singola) per tutte e
// tre. Verificare via GET {CONTACTI_URL}/wp-json/gf/v2/forms/11 prima di andare in produzione.
const useYupValidationResolver = () =>
  yup.object({
    input_1: yup.string().when("input_6_1", {
      is: true,
      then: yup.string(),
      otherwise: yup.string().required(),
    }), // nome - obbligatorio solo se non anonimo
    input_2: yup.string().when("input_6_1", {
      is: true,
      then: yup.string(),
      otherwise: yup.string().required(),
    }), // cognome - obbligatorio solo se non anonimo
    input_3: yup.string(), // azienda (opzionale)
    input_4: yup.string().when("input_6_1", {
      is: true,
      then: yup.string(),
      otherwise: yup.string().email().required(),
    }), // mail - obbligatoria solo se non anonimo
    input_5: yup.string(), // telefono (opzionale)
    input_6_1: yup.boolean(), // i wish to remain anonymous
    input_8: yup.string().required(), // product/service name / family / model
    input_9: yup.string().required(), // product/service code
    input_12: yup.string().required(), // product/service serial number
    input_13: yup.string().required(), // firmware/software/bsp version
    input_14: yup.string(), // hw version (opzionale)
    input_15: yup.string().required(), // type of security vulnerability
    input_16: yup.string().required(), // vulnerability description
    input_17: yup.string().required(), // reproduction steps
    input_18: yup.string().required(), // impact
    input_19: yup.string().required(), // attack vector
    input_21: yup.string(), // attachments (stringa composta con downloadUrl/objectKey degli upload R2)
    input_22: yup.string(), // notes (opzionale)
    input_23_1: yup.boolean(), // i would like to be named in the event of a Security Advisory
    input_24_1: yup.boolean().when("input_6_1", {
      is: true,
      then: yup.boolean(),
      otherwise: yup.boolean().equals([true]),
    }), // data use policy - obbligatoria solo se non anonimo
  })
  .required()

const MAX_ATTACHMENTS = 5
const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024 // 25 MB
const ALLOWED_ATTACHMENT_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"]

const getFileExtension = filename => {
  const match = /\.([a-zA-Z0-9]+)$/.exec(filename || "")
  return match ? match[1].toLowerCase() : ""
}

const ContactFormVulnerability = props => {
  const { t } = useTranslation()

  const resolver = useYupValidationResolver()

  const [attachments, setAttachments] = useState([])

  const attachmentStatusLabel = status => {
    switch (status) {
      case "uploading": return t("Caricamento in corso...")
      case "done": return t("Caricato")
      case "error": return t("Errore")
      default: return t("In attesa")
    }
  }

  const {
    reset,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onSubmit', // This will trigger validation only on submit
    shouldUnregister: false, // i valori restano in memoria anche a campo nascosto; l'esclusione dal payload anonimo è gestita esplicitamente in onSubmit
    resolver: yupResolver(resolver),
    defaultValues: {
      input_1: "",
      input_2: "",
      input_3: "",
      input_4: "",
      input_5: "",
      input_6_1: false,
      input_8: "",
      input_9: "",
      input_12: "",
      input_13: "",
      input_14: "",
      input_15: "",
      input_16: "",
      input_17: "",
      input_18: "",
      input_19: "",
      input_21: "",
      input_22: "",
      input_23_1: false,
      input_24_1: false,
    },
  })

  const formClass = fieldName => {
    return classNames("contact-form__form-row", {
      error: isError(fieldName),
    })
  }

  const isError = (fieldName, type) => {
    if (!type) {
      type = "required"
    }
    const error = !!errors && !!errors[fieldName]
    return error && errors[fieldName].type === type
  }

  const onSubmit = async data => {
    const payload = data.input_6_1
      ? { ...data, input_1: "", input_2: "", input_3: "", input_4: "", input_5: "", input_23_1: false }
      : data

    if (!!props.onSubmit) {
      await props.onSubmit(payload)
      reset()
    } else {
      console.error(payload)
      reset()
    }
  }

  // Governa la visibilità/obbligatorietà di Name, Company, Mail address, Telephone
  // e dei due flag finali (Security Advisory, Data Use Policy)
  const isAnonymous = watch("input_6_1")

  const uploadAttachment = async entry => {
    setAttachments(prev => prev.map(a => (a.id === entry.id ? { ...a, status: "uploading" } : a)))
    try {
      const presignRes = await fetch("/.netlify/functions/upload-presign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: entry.file.name,
          contentType: entry.file.type,
          size: entry.file.size,
          turnstileToken: props.turnstileToken,
        }),
      })
      const presignBody = await presignRes.json().catch(() => ({}))
      if (!presignRes.ok) {
        throw new Error(presignBody.error || `http_${presignRes.status}`)
      }
      const { uploadUrl, objectKey, downloadUrl } = presignBody

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: entry.file,
        headers: { "Content-Type": entry.file.type },
      })
      if (!putRes.ok) {
        throw new Error(`upload_http_${putRes.status}`)
      }

      setAttachments(prev =>
        prev.map(a => (a.id === entry.id ? { ...a, status: "done", objectKey, downloadUrl } : a))
      )
    } catch (e) {
      console.error(`Attachment upload error: ${e?.message}`)
      setAttachments(prev =>
        prev.map(a => (a.id === entry.id ? { ...a, status: "error", error: t("Caricamento fallito") } : a))
      )
    }
  }

  // Avvia l'upload di ogni allegato appena valido; se il captcha non è ancora
  // risolto, segnala l'errore invece di procedere.
  useEffect(() => {
    attachments
      .filter(a => a.status === "pending")
      .forEach(entry => {
        uploadAttachment(entry)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments])

  // Ricompone input_21 con l'elenco degli allegati caricati con successo.
  useEffect(() => {
    const composed = attachments
      .filter(a => a.status === "done")
      .map(a => `${a.name}: ${a.downloadUrl} (key: ${a.objectKey})`)
      .join("\n")
    setValue("input_21", composed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments])

  // Rimuove un allegato dalla lista lato client; se era già stato caricato su R2,
  // ne richiede anche la cancellazione lato server (best-effort, non bloccante).
  const handleRemoveAttachment = async id => {
    const entry = attachments.find(a => a.id === id)
    setAttachments(prev => prev.filter(a => a.id !== id))

    if (entry?.status === "done" && entry.objectKey) {
      try {
        await fetch("/.netlify/functions/delete-attachment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectKey: entry.objectKey }),
        })
      } catch (e) {
        console.error(`Attachment delete error: ${e?.message}`)
      }
    }
  }

  const handleAttachmentsChange = e => {
    const selected = Array.from(e.target.files || [])
    e.target.value = "" // permette di riselezionare lo stesso file
    if (selected.length === 0) return

    setAttachments(prev => {
      const next = [...prev]
      for (const file of selected) {
        if (next.length >= MAX_ATTACHMENTS) break
        const extension = getFileExtension(file.name)
        const entry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          name: file.name,
          status: "pending",
          error: null,
          objectKey: null,
          downloadUrl: null,
        }
        if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)) {
          entry.status = "error"
          entry.error = t("Formato file non ammesso")
        } else if (file.size > MAX_ATTACHMENT_SIZE) {
          entry.status = "error"
          entry.error = t("File troppo grande (max 25MB)")
        }
        next.push(entry)
      }
      return next
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!isAnonymous && (
        <>
          <div className="mb-6">
            <div className={formClass("input_1")}>
              <label className="contact-form__label" htmlFor="input_1">
                {t("Nome")}*
              </label>
              <input
                className="contact-form__input"
                type="text"
                id="input_1"
                {...register("input_1")}
              />
            </div>
            {!!isError("input_1") && (
              <span className="contact-form__error-message">
                *{t("Campo richiesto")}
              </span>
            )}
          </div>
          <div className="mb-6">
            <div className={formClass("input_2")}>
              <label className="contact-form__label" htmlFor="input_2">
                {t("Cognome")}*
              </label>
              <input
                className="contact-form__input"
                type="text"
                id="input_2"
                {...register("input_2")}
              />
            </div>
            {!!isError("input_2") && (
              <span className="contact-form__error-message">
                *{t("Campo richiesto")}
              </span>
            )}
          </div>
          <div className="mb-6">
            <div className={formClass("input_3")}>
              <label className="contact-form__label" htmlFor="input_3">
                {t("Azienda")}
              </label>
              <input
                className="contact-form__input"
                type="text"
                id="input_3"
                {...register("input_3")}
              />
            </div>
          </div>
          <div className="mb-6">
            <div className={formClass("input_4")}>
              <label className="contact-form__label" htmlFor="input_4">
                {t("Indirizzo email")}*
              </label>
              <input
                className="contact-form__input"
                type="email"
                id="input_4"
                {...register("input_4")}
              />
            </div>
            {!!isError("input_4") && (
              <span className="contact-form__error-message">
                *{t("Campo richiesto")}
              </span>
            )}
            {!!isError("input_4", "email") && (
              <span className="contact-form__error-message">
                *{t("controllare formattazione mail")}
              </span>
            )}
          </div>
          <div className="mb-6">
            <div className={formClass("input_5")}>
              <label className="contact-form__label" htmlFor="input_5">
                {t("Telefono")}
              </label>
              <input
                className="contact-form__input"
                type="tel"
                id="input_5"
                {...register("input_5")}
              />
            </div>
          </div>
        </>
      )}

      <div className="mb-6">
        <div className="contact-form__checkbox-row">
          <input
            type="checkbox"
            className="contact-form__checkbox"
            id="input_6_1"
            {...register("input_6_1")}
          />
          <label className="flex-grow-1" htmlFor="input_6_1">
            {t("Desidero rimanere anonimo")}
          </label>
        </div>
      </div>

      <div className="mb-6">
        <div className={formClass("input_8")}>
          <label className="contact-form__label" htmlFor="input_8">
            {t("Nome / Famiglia / Modello del Prodotto o Servizio")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_8"
            {...register("input_8", { required: true })}
          />
        </div>
        {!!isError("input_8") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_9")}>
          <label className="contact-form__label" htmlFor="input_9">
            {t("Codice Prodotto/Servizio")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_9"
            {...register("input_9", { required: true })}
          />
        </div>
        {!!isError("input_9") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_12")}>
          <label className="contact-form__label" htmlFor="input_12">
            {t("Numero di Serie Prodotto/Servizio")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_12"
            {...register("input_12", { required: true })}
          />
        </div>
        {!!isError("input_12") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_13")}>
          <label className="contact-form__label" htmlFor="input_13">
            {t("Versione Firmware/Software/BSP")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_13"
            {...register("input_13", { required: true })}
          />
        </div>
        {!!isError("input_13") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_14")}>
          <label className="contact-form__label" htmlFor="input_14">
            {t("Versione Hardware (se disponibile)")}
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_14"
            {...register("input_14")}
          />
        </div>
      </div>
      <div className="mb-6">
        <div className={formClass("input_15")}>
          <label className="contact-form__label" htmlFor="input_15">
            {t("Tipo di Vulnerabilità di Sicurezza")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_15"
            placeholder={t("es. DoS; se disponibili CWE-ID, CVE-ID")}
            {...register("input_15", { required: true })}
          />
        </div>
        {!!isError("input_15") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className="contact-form__form-col">
          <label className="contact-form__label" htmlFor="input_16">
            {t("Descrizione della Vulnerabilità")}*
          </label>
          <textarea
            className="contact-form__input w-100"
            rows="5"
            id="input_16"
            {...register("input_16", { required: true })}
          ></textarea>
        </div>
        {!!isError("input_16") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className="contact-form__form-col">
          <label className="contact-form__label" htmlFor="input_17">
            {t("Passaggi di Riproduzione")}*
          </label>
          <textarea
            className="contact-form__input w-100"
            rows="5"
            id="input_17"
            {...register("input_17", { required: true })}
          ></textarea>
        </div>
        {!!isError("input_17") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className="contact-form__form-col">
          <label className="contact-form__label" htmlFor="input_18">
            {t("Impatto")}*
          </label>
          <textarea
            className="contact-form__input w-100"
            rows="5"
            id="input_18"
            {...register("input_18", { required: true })}
          ></textarea>
        </div>
        {!!isError("input_18") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_19")}>
          <label className="contact-form__label" htmlFor="input_19">
            {t("Vettore di Attacco")}*
          </label>
          <input
            className="contact-form__input"
            type="text"
            id="input_19"
            {...register("input_19", { required: true })}
          />
        </div>
        {!!isError("input_19") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>
      <div className="mb-6">
        <div className={formClass("input_21")}>
          <label className="contact-form__label" htmlFor="input_21">
            {t("Allegati")}
          </label>
          <input type="hidden" id="input_21" {...register("input_21")} />
          {process.env.GATSBY_ENABLE_VULN_ATTACHMENTS === "true" ? (
            <>
              <div className="contact-form__file-upload">
                <label htmlFor="vuln-attachments-input" className="contact-form__file-button">
                  {t("Scegli file")}
                </label>
                <input
                  id="vuln-attachments-input"
                  className="contact-form__file-input-hidden"
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleAttachmentsChange}
                />
                <span className="contact-form__file-hint">
                    {t("PDF, PNG, JPG, JPEG — max 25MB")}
                </span>
              </div>
              {attachments.length > 0 && (
                <ul className="contact-form__attachments-list">
                  {attachments.map(a => (
                    <li key={a.id} className="contact-form__attachment-item">
                      <span>{a.name} — {attachmentStatusLabel(a.status)}</span>
                      {a.status === "error" && a.error && (
                        <span className="contact-form__error-message"> *{a.error}</span>
                      )}
                      <button
                        type="button"
                        className="contact-form__attachment-remove"
                        onClick={() => handleRemoveAttachment(a.id)}
                        aria-label={t("Rimuovi allegato")}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: "0.5rem" }}
                      >
                        <CloseIcon style={{ width: "12px", height: "12px" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p>
              {t("Se hai materiale a supporto (screenshot, log, proof-of-concept), indicalo nella descrizione: il team PSIRT ti risponderà con le istruzioni per l'invio.")}
            </p>
          )}
        </div>
      </div>
      <div className="mb-6">
        <div className="contact-form__form-col">
          <label className="contact-form__label" htmlFor="input_22">
            {t("Note")}
          </label>
          <textarea
            className="contact-form__input w-100"
            rows="5"
            id="input_22"
            {...register("input_22")}
          ></textarea>
        </div>
      </div>

      {!isAnonymous && (
        <>
          <div className="mb-6">
            <div className="contact-form__checkbox-row">
              <input
                type="checkbox"
                className="contact-form__checkbox"
                id="input_23_1"
                {...register("input_23_1")}
              />
              <label className="flex-grow-1" htmlFor="input_23_1">
                {t("Vorrei essere citato in caso di Security Advisory")}
              </label>
            </div>
          </div>
        </>
      )}
    
      <div className="mb-6">
        <div className="contact-form__checkbox-row">
          <input
            type="checkbox"
            className="contact-form__checkbox"
            id="input_24_1"
            {...register("input_24_1")}
          />
          <label
            className="flex-grow-1 terms-privacy-form-accept"
            htmlFor="input_24_1"
          >
            {t("Ho letto e compreso la")}{" "}
            <UniversalLink to={props.dedicatedPrivacyPolicyLink} target="_blank">
              {t("Data Use Policy")}
            </UniversalLink>
            {t(" e la ")}
            <UniversalLink to="https://www.gefran.com/privacy-policy/" target="_blank">
              {t("Privacy Policy")}*
            </UniversalLink>
          </label>
        </div>
        {!!isError("input_24_1", "oneOf") && (
          <span className="contact-form__error-message">
            *{t("Campo richiesto")}
          </span>
        )}
      </div>

      <div className="mb-80">
        <button
          type="submit"
          className="contact-form__submit"
          disabled={isSubmitting}
        >
          {t("Invia segnalazione")}
        </button>
      </div>
    </form>
  )
}

export default ContactFormVulnerability
